/**
 * PlanCard Component
 * Displays a subscription plan with dynamic styling based on metadata
 */

import React from 'react';
import {
  Zap,
  Crown,
  Shield,
  CheckCircle2,
  LucideIcon,
  Sparkles,
  Star,
  Rocket,
  Award,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import type { Package, PackageMetadata, ScheduledPlanChange } from '../../types';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utils/formatDate';
import { pickLocalized, pickLocalizedList } from '../../utils/planChange';
import type { PlanAction } from '../../utils/planChange';

const COLLAPSED_BENEFIT_COUNT = 6;

interface PlanCardProps {
  package: Package;
  action: PlanAction;
  scheduledChange?: ScheduledPlanChange | null;
  selectedCurrency?: 'USD' | 'VND';
  highlighted?: boolean;
  onUpgrade: (pkg: Package) => void;
  onDowngrade: (pkg: Package) => void;
  onCancel: () => void;
  onResume: () => void;
}

// Map icon name strings to Lucide components
const iconMap: Record<string, LucideIcon> = {
  Zap,
  Crown,
  Shield,
  Sparkles,
  Star,
  Rocket,
  Award,
  CheckCircle2,
};

export const PlanCard: React.FC<PlanCardProps> = ({
  package: pkg,
  action,
  scheduledChange,
  selectedCurrency = 'USD',
  highlighted = false,
  onUpgrade,
  onDowngrade,
  onCancel,
  onResume,
}) => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = React.useState(false);
  // Parse metadata
  const metadata: PackageMetadata = React.useMemo(() => {
    try {
      return JSON.parse(pkg.metadata ?? '{}') as PackageMetadata;
    } catch {
      return { color: '#3B82F6', icon: 'Zap' };
    }
  }, [pkg.metadata]);

  const IconComponent = iconMap[metadata.icon] || Zap;
  const accentColor = metadata.color || '#3B82F6';
  const isFreePlan = pkg.code.toUpperCase() === 'FREE';
  const features = pkg.features ?? [];
  const description = pickLocalized(pkg.description, i18n.language);
  const highlights = pickLocalizedList(pkg.highlights, i18n.language);
  const isCurrentPlan = action === 'current';
  const isScheduledPlan = action === 'scheduled';
  const formattedScheduledDate = scheduledChange
    ? formatDate(scheduledChange.effectiveAt, i18n.language)
    : '';

  // Resolve primary price
  const primaryPrice = React.useMemo(() => {
    if (!pkg.prices || pkg.prices.length === 0) {
      return null;
    }
    const matched = pkg.prices.find(
      (p) => (!selectedCurrency || p.currency === selectedCurrency) && p.isActive !== false
    );
    return matched || pkg.prices[0];
  }, [pkg.prices, selectedCurrency]);

  const displayPriceString = React.useMemo(() => {
    if (isFreePlan) {
      return selectedCurrency === 'VND' ? '0 ₫' : '$0';
    }

    if (primaryPrice) {
      const value = Number(primaryPrice.amountMinor);
      const displayValue = primaryPrice.currency === 'USD' ? value / 100 : value;

      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: primaryPrice.currency,
        maximumFractionDigits: primaryPrice.currency === 'VND' ? 0 : 2,
      }).format(displayValue);
    }

    if (typeof pkg.price === 'number') {
      return `$${pkg.price}`;
    }

    return '$0';
  }, [isFreePlan, pkg.price, primaryPrice, selectedCurrency]);

  const secondaryPriceString = React.useMemo(() => {
    if (isFreePlan || !pkg.prices || pkg.prices.length <= 1) {
      return null;
    }

    const secondary = pkg.prices.find(
      (p) => p.publicId !== primaryPrice?.publicId && p.isActive !== false
    );
    if (!secondary) return null;

    const value = Number(secondary.amountMinor);
    const displayValue = secondary.currency === 'USD' ? value / 100 : value;

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: secondary.currency,
      maximumFractionDigits: secondary.currency === 'VND' ? 0 : 2,
    }).format(displayValue);
  }, [isFreePlan, pkg.prices, primaryPrice?.publicId]);

  const benefits = [
    ...highlights.map((highlight, index) => ({
      key: `highlight-${index}`,
      label: highlight,
    })),
    isFreePlan
      ? {
          key: 'default-agent',
          label: t('subscription.plan.defaultAgentOnly'),
        }
      : {
          key: 'included-usage',
          label: t('subscription.plan.includedUsage'),
        },
    ...(isFreePlan
      ? []
      : [
          {
            key: 'agents',
            label: t('subscription.plan.upToAgents', { count: pkg.maxAgents }),
          },
        ]),
    ...(pkg.capabilities ?? []).map((capability) => ({
      key: `capability-${capability}`,
      label: t(`subscription.capabilities.${capability}`, capability),
    })),
    ...features.map((code) => ({
      key: `feature-${code}`,
      label: t(`packageFeatures.names.${code}`, code),
    })),
    ...(pkg.includesAdvanceTokens
      ? [
          {
            key: 'includes-advance-tokens',
            label: t('subscription.includesAdvanceTokens'),
          },
        ]
      : []),
    {
      key: 'billing-cycle',
      label: t('subscription.plan.billingCycle', { days: pkg.duration }),
    },
  ];

  const hasMoreBenefits = benefits.length > COLLAPSED_BENEFIT_COUNT;
  const visibleBenefits =
    expanded || !hasMoreBenefits ? benefits : benefits.slice(0, COLLAPSED_BENEFIT_COUNT);

  return (
    <div
      className={`
        relative flex h-full flex-col rounded-lg border-2 bg-white p-6 transition-all
        duration-200 hover:shadow-lg dark:bg-slate-800
        ${
          isCurrentPlan || isScheduledPlan
            ? 'shadow-md'
            : 'border-gray-200 hover:border-blue-300 dark:border-slate-700 dark:hover:border-blue-600'
        }
        ${highlighted ? 'ring-2 ring-amber-400 ring-offset-2 dark:ring-offset-slate-900' : ''}
      `}
      style={{
        borderColor: isCurrentPlan ? accentColor : isScheduledPlan ? '#F59E0B' : undefined,
        borderTopWidth: isCurrentPlan || isScheduledPlan ? '4px' : undefined,
      }}
    >
      {(isCurrentPlan || isScheduledPlan) && (
        <div
          className={`absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-xs font-semibold text-white ${
            isScheduledPlan ? 'bg-amber-500' : ''
          }`}
          style={{ backgroundColor: isScheduledPlan ? undefined : accentColor }}
        >
          {isScheduledPlan
            ? t('subscription.plan.startsOn', { date: formattedScheduledDate })
            : t('subscription.plan.currentPlan')}
        </div>
      )}

      {/* Icon and Name */}
      <div className="mb-4 flex items-center gap-3">
        <div className="rounded-lg p-3" style={{ backgroundColor: `${accentColor}20` }}>
          <IconComponent size={28} style={{ color: accentColor }} className="stroke-current" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-slate-100">{pkg.name}</h3>
          {description && (
            <p className="mt-1 text-sm text-gray-600 dark:text-slate-400">{description}</p>
          )}
        </div>
      </div>

      {/* Price */}
      <div className="mb-6">
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-bold text-gray-900 dark:text-slate-100">
            {displayPriceString}
          </span>
          <span className="text-gray-500 dark:text-slate-400">
            {t('subscription.plan.pricePeriod', { days: pkg.duration })}
          </span>
        </div>
        {secondaryPriceString && (
          <div className="mt-1 text-xs text-gray-500 dark:text-slate-400">
            ({secondaryPriceString})
          </div>
        )}
      </div>

      <ul className={`mb-3 space-y-3 ${expanded ? 'max-h-80 overflow-y-auto pr-1' : ''}`}>
        {visibleBenefits.map((benefit) => (
          <li key={benefit.key} className="flex items-start gap-3">
            <CheckCircle2
              size={20}
              className="mt-0.5 flex-shrink-0 text-green-500 dark:text-green-400"
            />
            <span className="text-sm text-gray-700 dark:text-slate-300">{benefit.label}</span>
          </li>
        ))}
      </ul>

      {/* Pinned to the card bottom so toggle + actions line up across cards */}
      <div className="mt-auto pt-3">
        {hasMoreBenefits && (
          <Button
            variant="text"
            size="small"
            onClick={() => setExpanded((value) => !value)}
            fullWidth
            className="mb-4 normal-case"
            endIcon={expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            sx={{ color: accentColor }}
          >
            {expanded
              ? t('subscription.plan.showFewerFeatures')
              : t('subscription.plan.showAllFeatures')}
          </Button>
        )}

        {isCurrentPlan && scheduledChange && (
          <div className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            <p>
              {scheduledChange.reason === 'trial_end'
                ? t('subscription.plan.trialEndsThen', {
                    date: formattedScheduledDate,
                    name: scheduledChange.packageName,
                  })
                : t('subscription.plan.activeUntilThen', {
                    date: formattedScheduledDate,
                    name: scheduledChange.packageName,
                  })}
            </p>
            {scheduledChange.reason !== 'trial_end' && (
              <Button
                variant="outlined"
                onClick={onResume}
                fullWidth
                className="mt-3 normal-case"
                sx={{
                  borderColor: accentColor,
                  color: accentColor,
                  '&:hover': {
                    borderColor: accentColor,
                    backgroundColor: `${accentColor}12`,
                  },
                }}
              >
                {t('subscription.plan.keepPlan', { name: pkg.name })}
              </Button>
            )}
          </div>
        )}

        {/* Action Button */}
        {isCurrentPlan && scheduledChange ? null : isCurrentPlan && isFreePlan ? (
          <Button variant="text" disabled fullWidth className="normal-case">
            {t('subscription.plan.currentPlan')}
          </Button>
        ) : isCurrentPlan ? (
          <>
            <Button
              variant="outlined"
              onClick={() => onUpgrade(pkg)}
              fullWidth
              className="normal-case"
              sx={{ borderColor: accentColor, color: accentColor, mb: 1 }}
            >
              {t('subscription.plan.renew', 'Extend plan')}
            </Button>
            <Button
              variant="text"
              onClick={onCancel}
              fullWidth
              className="normal-case"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              {t('subscription.plan.cancelSubscription')}
            </Button>
          </>
        ) : isScheduledPlan ? (
          <Button variant="outlined" disabled fullWidth className="normal-case">
            {t('subscription.plan.scheduled')}
          </Button>
        ) : action === 'downgrade' ? (
          <Button
            variant="outlined"
            onClick={() => onDowngrade(pkg)}
            fullWidth
            className="normal-case"
            sx={{
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'text.secondary',
                backgroundColor: 'action.hover',
              },
            }}
          >
            {t('subscription.plan.downgradePlan')}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => onUpgrade(pkg)}
            fullWidth
            className="normal-case"
            sx={{
              backgroundColor: accentColor,
              color: '#fff',
              '&:hover': {
                backgroundColor: accentColor,
                opacity: 0.9,
              },
            }}
          >
            {t('subscription.plan.upgradePlan')}
          </Button>
        )}
      </div>
    </div>
  );
};
